import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
// Mui Imports
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
// Icons
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeIcon from "@mui/icons-material/DateRange";

import TableWithPaginationSorting from "../../../../../../Common/Table/TableWithPaginationSorting";
import KMKDateRangePicker from "../../../../../../Common/KMKDateRangePicker";

import WorkspaceService from "../../../../../../../service/workspace";
import { showToast } from "../../../../../../../store/actions";

import { convertTimezone } from "../../../../../../../util/common";
import { regexName } from "../../../../../../../util/regularExpress";
import { dayjs } from "../../../../../../../util/dayjs";
import { Detailed_Columns, rowsPerPageOptions, Job_Status_Types } from "../../../../utils/JobData";

const DetailedHistory = (props) => {
  const { Workspace_id } = useParams();
  const { historyDetails } = props;

  const { editJob } = useSelector((state) => state.dataPipeline);
  const { id } = editJob;

  const user = useSelector((state) => state.user);
  const { UserTimezone } = useMemo(() => user?.currentUser || {}, [user]);

  const Job_id = parseInt(id);
  const jobType = useMemo(() => Number(editJob?.data?.type || 1), [editJob?.data?.type]);

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [search, setSearch] = useState("");
  const [detailedOptions, setDetailedOptions] = useState({
    limit: 50,
    page: 0,
    orderBy: "LOAD_DATE",
    order: "desc",
    search: "",
  });
  const [detailCount, setDetailCount] = useState(0);
  const [detailedHistory, setDetailedHistory] = useState([]);

  const detailedOptionsRef = useRef(detailedOptions);
  const dispatch = useDispatch();

  const formatDate = useCallback((date, displayFormat) => {
    return displayFormat ? date.format("MM/DD/YYYY HH:mm:ss") : date.format("YYYY-MM-DD HH:mm:ss");
  }, []);

  const disableDetailedDatePicker = useCallback(
    (current) => {
      if (historyDetails?.dateRange) {
        return (
          current &&
          (current < dayjs(historyDetails?.dateRange[0]).startOf("day") ||
            (historyDetails?.dateRange[1] ? current > dayjs(historyDetails?.dateRange[1]).endOf("day") : false))
        );
      }
      return false;
    },
    [historyDetails?.dateRange]
  );

  // Get Detailed History List
  const getDetailedHistoryList = useCallback(
    async (dates, tableOptions = detailedOptionsRef.current, jobHistory_id = historyDetails?.jobHistory_id) => {
      setLoading(true);

      const wsService = new WorkspaceService();
      const result = await wsService.getDetailedJobHistory(
        Workspace_id,
        Job_id,
        dates && dates[0] ? formatDate(dates[0]) : undefined,
        dates && dates[1] ? formatDate(dates[1]) : undefined,
        { ...tableOptions, UserTimezone },
        jobHistory_id
      );

      if (result.success) {
        const { historyData, count } = result.data;
        let historyDataArr = [];

        historyData.forEach((row) => {
          historyDataArr.push([
            ...(jobType !== 6
              ? [
                  {
                    value: row.STORED_PROCEDURE,
                    align: "left",
                  },
                ]
              : []),
            { value: row.STEP, align: "left" },
            {
              value: row.RESULT,
              align: "left",
              color:
                row.Status?.toLowerCase() === "failed" || row.Status?.toLowerCase() === "error"
                  ? Job_Status_Types[row.Status?.toLowerCase()]?.color
                  : "black",
              message: row.RESULT,
            },
            { value: row.Status, align: "left", color: Job_Status_Types[row.Status?.toLowerCase()]?.color },
            {
              value: row.LOAD_DATE ? convertTimezone(row.LOAD_DATE) : null,
              align: "left",
            },
          ]);
        });
        setDetailedHistory(historyDataArr);
        setDetailCount(count);
      } else setDetailCount(0);

      setLoading(false);
    },
    [Job_id, UserTimezone, Workspace_id, formatDate, historyDetails?.jobHistory_id, jobType]
  );

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setDetailedOptions({ ...detailedOptions, search, page: 0 });
      getDetailedHistoryList(dateRange, { ...detailedOptions, search, page: 0 });
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setDetailedOptions({ ...detailedOptions, search: "", page: 0 });
    getDetailedHistoryList(dateRange, { ...detailedOptions, search: "", page: 0 });
  };

  const getDetailedDataUsingOptions = (tableOptions) => {
    getDetailedHistoryList(dateRange, { ...tableOptions, search });
  };

  useEffect(() => {
    setSearch("");
    const updatedOptions = { limit: 50, page: 0, orderBy: "LOAD_DATE", order: "desc", search: "" };
    setDateRange(null);
    setDetailedOptions(updatedOptions);
    getDetailedHistoryList(undefined, updatedOptions);
    return () => {};
  }, [getDetailedHistoryList, historyDetails?.jobHistory_id]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <KMKDateRangePicker
          format={(value) => value.format("MM/DD/YYYY HH:mm:ss")}
          value={dateRange}
          defaultValue={historyDetails?.dateRange}
          onChange={(dates) => {
            if (dates) {
              if (
                historyDetails?.dateRange[0] > dates[0] ||
                (historyDetails?.dateRange[1] !== null ? historyDetails?.dateRange[1] < dates[1] : false)
              ) {
                dispatch(
                  showToast({
                    message: "Datetime selection is not in the scope of this job run.",
                    type: "error",
                  })
                );
              } else {
                setDateRange(dates);
                setDetailedOptions({ ...detailedOptions, search, page: 0 });
                getDetailedHistoryList(dates, { ...detailedOptions, search, page: 0 }, historyDetails?.jobHistory_id);
              }
            } else {
              let tempDates = true ? dates : historyDetails?.dateRange;
              setDateRange(tempDates);
              setDetailedOptions({ ...detailedOptions, search, page: 0 });
              getDetailedHistoryList(tempDates, { ...detailedOptions, search, page: 0 }, historyDetails?.jobHistory_id);
            }
          }}
          disabledDate={disableDetailedDatePicker}
          size="middle"
          separator="~"
          allowClear={<ClearIcon sx={{ fontSize: "16px" }} />}
          suffixIcon={dateRange ? <ClearIcon sx={{ fontSize: "16px" }} /> : <DateRangeIcon sx={{ fontSize: "16px" }} />}
          variant="outlined"
          showTime
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          value={search}
          onKeyDown={handleSearch}
          onChange={(e) => {
            if (regexName(e.target.value, "customSpecialChar", " _:.-")) setSearch(e.target.value);
            if (e.target.value === "") setDetailedOptions({ ...detailedOptions, page: 0 });
          }}
          placeholder="Search..."
          variant="standard"
          inputProps={{ maxLength: 50 }}
          InputProps={{
            startAdornment: <SearchIcon color="primary" />,
            ...(search &&
              !loading && {
                endAdornment: (
                  <IconButton onClick={handleClearSearch} size="small" disabled={loading}>
                    <ClearIcon color="primary" />
                  </IconButton>
                ),
              }),
          }}
          disabled={loading}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        />
      </Grid>
      <Grid size={12}>
        <TableWithPaginationSorting
          columns={Detailed_Columns.filter((col) => !(jobType === 6 && col.id === "STORED_PROCEDURE"))}
          tableData={detailedHistory}
          ellipsisColumns={jobType === 6 ? [2] : [3]}
          tableMaxWidth="100%"
          tableMaxHeight="100%"
          noDataColspan={jobType === 6 ? 4 : 5}
          noDataMessage="No records found"
          handleRequest={getDetailedDataUsingOptions}
          rowsPerPageOptions={rowsPerPageOptions}
          defaultOrder="desc"
          defaultOrderBy="LOAD_DATE"
          options={detailedOptions}
          setOptions={setDetailedOptions}
          count={detailCount}
          showCopy={true}
          loading={loading}
        />
      </Grid>
    </Grid>
  );
};

DetailedHistory.propTypes = {
  historyDetails: PropTypes.object.isRequired,
};

export default DetailedHistory;
